import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { FiArrowLeft, FiPackage, FiTruck, FiCheckCircle, FiMapPin, FiClock, FiRefreshCw } from 'react-icons/fi';
import { useShopping } from '../context/ShoppingContext';
import { useToast } from '../context/ToastContext';
import '../styles/LiveTracking.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_FIREBASE_API_KEY || '';

// Store location (Ghaziabad)
const STORE_LOCATION = { lat: 28.6502545, lng: 77.4235014 };

const STORE_ICON_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32" width="36" height="48"><path fill="#e74c3c" d="M12,0 C5.37,0 0,5.37 0,12 C0,21 12,32 12,32 C12,32 24,21 24,12 C24,5.37 18.63,0 12,0 Z"/><circle cx="12" cy="11.5" r="8" fill="#ffffff"/><g transform="translate(5.5, 5) scale(0.55)"><path fill="#e74c3c" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z"/></g></svg>')}`;

const TRACKING_STEPS = [
    { key: 'ordered', label: 'Order Placed', icon: FiPackage },
    { key: 'pickup', label: 'Picked Up', icon: FiPackage },
    { key: 'transit', label: 'In Transit', icon: FiTruck },
    { key: 'out_delivery', label: 'Out for Delivery', icon: FiTruck },
    { key: 'delivered', label: 'Delivered', icon: FiCheckCircle },
];

function getStepIndex(status) {
    const s = (status || '').toLowerCase();

    // Delivered (Step 4)
    if (s.includes('deliver') && !s.includes('out')) return 4;

    // Out for Delivery (Step 3) - Rider Reached Drop
    if (s.includes('out for delivery') || s.includes('out_for_delivery') || s.includes('reach') || s.includes('drop')) return 3;

    // In Transit (Step 2) - Picked Up
    if (s.includes('transit') || s.includes('shipped') || s.includes('picked up')) return 2;

    // Picked Up (Step 1) - Rider Reached Pickup, Rider Assigned, Searching For Rider
    if (s.includes('pick') || s.includes('manifest') || s.includes('assign') || s.includes('search')) return 1;

    return 0; // Order Placed
}

function LiveTracking() {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [orderData, setOrderData] = useState(null);
    const [trackingInfo, setTrackingInfo] = useState(null);
    const [directions, setDirections] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const { user, authLoading } = useShopping();

    const fetchTracking = useCallback(async (showRefresh = false) => {
        try {
            if (showRefresh) setRefreshing(true);
            const token = user ? await user.getIdToken() : localStorage.getItem('token');
            if (!token) return; // Wait for token to load

            const response = await fetch(`${API_URL}/orders/${orderId}/tracking`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to fetch tracking data');
            }

            const data = await response.json();
            setOrderData(data.order);
            setTrackingInfo(data.trackingInfo);
            setLastRefresh(new Date());
            setError(null);
        } catch (err) {
            console.error("Live Tracking Error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [orderId, user]);

    useEffect(() => {
        if (authLoading) return; // wait for user auth state to resolve

        const token = user ? user.accessToken : localStorage.getItem('token');
        if (!token && !user) {
            toast.error('Please login to track your order');
            navigate('/account', { state: { from: location.pathname } });
            return;
        }

        fetchTracking();
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => fetchTracking(), 30000);
        return () => clearInterval(interval);
    }, [fetchTracking, user, authLoading, navigate, toast, location.pathname]);

    // Calculate rider position along route based on status
    const getRiderPosition = useCallback(() => {
        if (!orderData?.shippingAddress || !trackingInfo) return STORE_LOCATION;

        const deliveryLat = parseFloat(orderData.shippingAddress.latitude) || STORE_LOCATION.lat + 0.02;
        const deliveryLng = parseFloat(orderData.shippingAddress.longitude) || STORE_LOCATION.lng + 0.02;

        const currentStep = getStepIndex(trackingInfo.currentStatus);

        // Interpolate position between store and delivery based on step
        const progress = Math.min(currentStep / 4, 1);
        return {
            lat: STORE_LOCATION.lat + (deliveryLat - STORE_LOCATION.lat) * progress,
            lng: STORE_LOCATION.lng + (deliveryLng - STORE_LOCATION.lng) * progress
        };
    }, [orderData, trackingInfo]);

    const deliveryLocation = orderData?.shippingAddress ? {
        lat: parseFloat(orderData.shippingAddress.latitude) || STORE_LOCATION.lat + 0.02,
        lng: parseFloat(orderData.shippingAddress.longitude) || STORE_LOCATION.lng + 0.02
    } : null;

    const currentStep = trackingInfo ? getStepIndex(trackingInfo.currentStatus) : 0;

    if (loading) {
        return (
            <div className="live-tracking-page">
                <div className="tracking-loading">
                    <div className="tracking-spinner"></div>
                    <p>Loading tracking data...</p>
                </div>
            </div>
        );
    }

    if (error && !orderData) {
        return (
            <div className="live-tracking-page">
                <div className="tracking-error">
                    <h2>Unable to load tracking</h2>
                    <p>{error}</p>
                    <button onClick={() => navigate('/orders')}>Back to Orders</button>
                </div>
            </div>
        );
    }

    return (
        <div className="live-tracking-page">
            {/* Header */}
            <div className="tracking-header">
                <button className="tracking-back-btn" onClick={() => navigate('/orders')}>
                    <FiArrowLeft size={20} />
                </button>
                <div className="tracking-header-info">
                    <h2>Live Tracking</h2>
                    <span className="tracking-order-id">Order #{orderData?.orderId?.slice(-8)}</span>
                </div>
                <button
                    className={`tracking-refresh-btn ${refreshing ? 'spinning' : ''}`}
                    onClick={() => fetchTracking(true)}
                    disabled={refreshing}
                >
                    <FiRefreshCw size={18} />
                </button>
            </div>

            {/* Map Section */}
            <div className="tracking-map-container">
                {GOOGLE_MAPS_KEY ? (
                    <LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY}>
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={getRiderPosition()}
                            zoom={13}
                            options={{
                                zoomControl: true,
                                streetViewControl: false,
                                mapTypeControl: false,
                                fullscreenControl: false,
                                styles: [
                                    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                                    { featureType: 'transit', stylers: [{ visibility: 'off' }] }
                                ]
                            }}
                        >
                            {/* Store Marker */}
                            <Marker
                                position={STORE_LOCATION}
                                icon={STORE_ICON_URL}
                                title="Store"
                            />

                            {/* Delivery Location Marker */}
                            {deliveryLocation && (
                                <Marker
                                    position={deliveryLocation}
                                    title="Delivery Address"
                                />
                            )}

                            {/* Rider Marker */}
                            {currentStep > 0 && currentStep < 4 && (
                                <Marker
                                    position={getRiderPosition()}
                                    label={{ text: '🚴', fontSize: '28px' }}
                                    title="Rider"
                                />
                            )}

                            {directions && <DirectionsRenderer directions={directions} />}
                        </GoogleMap>
                    </LoadScript>
                ) : (
                    <div className="tracking-map-placeholder">
                        <FiMapPin size={48} />
                        <p>Map view requires Google Maps API key</p>
                        <div className="map-locations">
                            <div className="map-location-item">
                                <span className="map-dot store"></span>
                                <span>Store: Ghaziabad, UP</span>
                            </div>
                            <div className="map-location-item">
                                <span className="map-dot delivery"></span>
                                <span>Delivery: {orderData?.shippingAddress?.city || 'Customer Address'}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Current Status Banner */}
            <div className={`tracking-status-banner step-${currentStep}`}>
                <div className="status-banner-icon">
                    {currentStep >= 4 ? <FiCheckCircle size={24} /> : <FiTruck size={24} />}
                </div>
                <div className="status-banner-text">
                    <h3>{trackingInfo?.currentStatus || orderData?.status || 'Processing'}</h3>
                    <p>
                        {trackingInfo?.etd && !trackingInfo.etd.includes('0001-01-01') ? `Expected: ${new Date(trackingInfo.etd).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}` :
                            orderData?.deliveryType === 'quick' ? 'Quick Delivery - Same Day' : 'Standard Delivery'}
                    </p>
                </div>
            </div>

            {/* Progress Stepper */}
            <div className="tracking-stepper">
                {TRACKING_STEPS.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = index <= currentStep;
                    const isCurrent = index === currentStep;
                    return (
                        <div key={step.key} className={`stepper-item ${isActive ? 'active' : ''} ${isCurrent ? 'current' : ''}`}>
                            <div className="stepper-icon">
                                <Icon size={16} />
                            </div>
                            <span className="stepper-label">{step.label}</span>
                            {index < TRACKING_STEPS.length - 1 && <div className={`stepper-line ${index < currentStep ? 'filled' : ''}`} />}
                        </div>
                    );
                })}
            </div>

            {/* Shipping Details Card */}
            {(orderData?.shippingDetails || trackingInfo?.shipmentTrack?.[0]) && (
                <div className="tracking-details-card">
                    <h3>Shipping Details</h3>
                    <div className="detail-row">
                        <span className="detail-label">Courier</span>
                        <span className="detail-value" style={{ textTransform: 'capitalize' }}>
                            {(() => {
                                const courierId = trackingInfo?.shipmentTrack?.[0]?.courier_company_id;
                                const courierName = orderData?.shippingDetails?.courier_name;

                                // Internal Shiprocket IDs mapping
                                const courierMap = {
                                    '959': 'Porter',
                                    '1': 'Delhivery',
                                    '2': 'XpressBees',
                                    '10': 'Ecom Express',
                                    '30': 'Shadowfax',
                                    '8': 'Bluedart',
                                    '11': 'DotZot'
                                };

                                return courierName || (courierId ? courierMap[String(courierId)] || `Courier #${courierId}` : 'Assigning...');
                            })()}
                        </span>
                    </div>
                    {(orderData?.tracking?.awb || orderData?.shippingDetails?.awb_code) && (
                        <div className="detail-row">
                            <span className="detail-label">AWB</span>
                            <span className="detail-value">{orderData?.tracking?.awb || orderData?.shippingDetails?.awb_code}</span>
                        </div>
                    )}
                    <div className="detail-row">
                        <span className="detail-label">Delivery To</span>
                        <span className="detail-value">
                            {orderData?.shippingAddress?.fullName}, {orderData?.shippingAddress?.city}
                        </span>
                    </div>
                </div>
            )}

            {/* Activity Timeline */}
            {trackingInfo?.shipmentTrackActivities?.length > 0 && (
                <div className="tracking-timeline-card">
                    <h3>Tracking Activity</h3>
                    <div className="timeline">
                        {(() => {
                            const formatTrackingStatus = (status) => {
                                if (!status || status === 'NA' || status === 'N/A') return null;
                                const s = status.toLowerCase();
                                if (s === 'planned') return 'Order Created In The System';
                                if (s === 'ordercreated') return 'Searching For Rider';
                                if (s === 'assigned') return 'Rider Assigned';
                                if (s === 'arrived') return 'Rider Reached Pickup';
                                if (s === 'started') return 'Picked Up';
                                if (s === 'ontheway') return 'On The Way';
                                if (s === 'reached') return 'Rider Reached Drop';
                                if (s === 'success' || s === 'delivered') return 'Order Delivered';
                                if (s === 'failed') return 'Searching For Rider (Retry)';

                                return status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, str => str.toUpperCase()).trim();
                            };

                            // Oldest at the top (chronological)
                            const activities = [...(trackingInfo.shipmentTrackActivities || [])];

                            return activities.map((activity, index) => (
                                <div key={index} className={`timeline-item ${index === activities.length - 1 ? 'latest' : ''}`}>
                                    <div className="timeline-dot" />
                                    <div className="timeline-content">
                                        <p className="timeline-activity">
                                            {formatTrackingStatus(activity['sr-status-label']) ||
                                                formatTrackingStatus(activity.activity) ||
                                                formatTrackingStatus(activity.status) ||
                                                'Status Updated'}
                                        </p>
                                        <p className="timeline-location">{activity.location || activity.city || ''}</p>
                                        <p className="timeline-date">
                                            {(() => {
                                                if (!activity.date) return '';

                                                const rawDateStr = String(activity.date).trim();
                                                if (rawDateStr.toLowerCase() === 'invalid date') return '';

                                                const d = new Date(rawDateStr);
                                                if (isNaN(d.getTime())) {
                                                    return rawDateStr;
                                                }

                                                return d.toLocaleString('en-US', {
                                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                });
                                            })()}
                                        </p>
                                    </div>
                                </div>
                            ));
                        })()}
                    </div>
                </div>
            )}

            {/* No tracking data yet */}
            {!trackingInfo?.available && (
                <div className="tracking-pending-card">
                    <FiClock size={32} />
                    <h3>Tracking will be available soon</h3>
                    <p>Your order is being processed. Live tracking updates will appear here once the courier picks up your package.</p>
                    <p className="tracking-last-refresh">
                        {lastRefresh ? `Last checked: ${lastRefresh.toLocaleTimeString()}` : ''}
                    </p>
                </div>
            )}

            {/* Order Items */}
            <div className="tracking-items-card">
                <h3>Order Items</h3>
                {orderData?.items?.map((item, i) => (
                    <div key={i} className="tracking-item">
                        <img src={item.image || item.images?.[0]} alt={item.name} className="tracking-item-img" />
                        <div className="tracking-item-info">
                            <p className="tracking-item-name">{item.name}</p>
                            <p className="tracking-item-qty">Qty: {item.quantity}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default LiveTracking;
